import React, { useState } from "react"
import { useQuery } from "@apollo/client"
import { Dropdown, DropdownToggle, DropdownMenu, Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup, Alert } from "reactstrap"
import Select from "react-select"
import withApolloClient from "../../withApolloClient"
import ConfirmationPopup from "../../Material/ConfirmationPopup"
import { RepsTypeAhead } from "./TypeAheadComponents"
import { TaskStatusOptionsQuery } from "../Queries"
import { BulkTaskActionMutation } from "../Mutations"
import "./BulkTaskActionsBar.css"

const ACTION_LABELS = {
    UPDATE_STATUS: "Update Status",
    ASSIGN_REP: "Assign Rep",
    UNASSIGN_REP: "Unassign Rep",
    UPDATE_DUE_DATE: "Update Due Date",
    ARCHIVE: "Archive",
    UNARCHIVE: "Unarchive",
}

function BulkTaskActionsBar({ selectedTaskIds, boardId, onClearSelection, refetchTasksQuery, apolloClient, allSelectedArchived, allSelectedUnarchived }) {
    const [openModal, setOpenModal] = useState("")
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [status, setStatus] = useState("")
    const [dueDate, setDueDate] = useState("")
    const [repIds, setRepIds] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [successMessage, setSuccessMessage] = useState("")

    const { data: taskStatusData } = useQuery(TaskStatusOptionsQuery)
    const statusOptions = (taskStatusData?.optionsList?.enumValues || []).map((s) => ({
        value: s.name,
        label: s.description || s.name,
    }))

    const cancelModal = () => {
        setOpenModal("")
        setError("")
        setSuccessMessage("")
        setStatus("")
        setDueDate("")
        setRepIds([])
    }

    const clearSelectionAndReset = () => {
        cancelModal()
        onClearSelection && onClearSelection()
    }

    // ConfirmationPopup's "Close" button is the only exit from a confirmation modal, whether
    // the action succeeded, failed, or was never confirmed — so it's the one place that still
    // needs to decide at click-time which of the two resets above applies.
    const handleConfirmationClose = () => {
        if (successMessage) {
            clearSelectionAndReset()
        } else {
            cancelModal()
        }
    }

    const taskCountLabel = `${selectedTaskIds.length} task${selectedTaskIds.length === 1 ? "" : "s"}`

    const runBulkAction = (action, value) => {
        setLoading(true)
        setError("")
        setSuccessMessage("")
        return apolloClient.mutate({
            mutation: BulkTaskActionMutation,
            variables: {
                input: {
                    taskIds: selectedTaskIds,
                    action,
                    ...(value !== undefined ? { value } : {}),
                },
            },
        }).then((result) => {
            const errorMessage = result?.data?.bulkTaskAction?.errors?.[0]?.messages?.[0]
            setLoading(false)
            if (errorMessage) {
                setError(errorMessage)
                return false
            }
            setSuccessMessage(`${ACTION_LABELS[action]} applied to ${taskCountLabel}.`)
            refetchTasksQuery && refetchTasksQuery()
            return true
        }).catch((err) => {
            setLoading(false)
            setError(err.message || "Something went wrong")
            return false
        })
    }

    const handleConfirmedAction = (action) => {
        runBulkAction(action)
    }

    const handleUpdateStatus = () => {
        if (!status) {
            setError("Please select a status")
            return
        }
        runBulkAction("UPDATE_STATUS", status).then((ok) => {
            if (ok) clearSelectionAndReset()
        })
    }

    const handleUpdateDueDate = () => {
        if (!dueDate) {
            setError("Please select a due date")
            return
        }
        runBulkAction("UPDATE_DUE_DATE", dueDate).then((ok) => {
            if (ok) clearSelectionAndReset()
        })
    }

    const handleAssignRep = () => {
        if (repIds.length === 0) {
            setError("Please select at least one rep")
            return
        }
        runBulkAction("ASSIGN_REP", repIds).then((ok) => {
            if (ok) clearSelectionAndReset()
        })
    }

    if (!selectedTaskIds || selectedTaskIds.length === 0) return null

    return (
        <div className="bulk-task-actions-bar">
            <span className="bulk-task-actions-count">{taskCountLabel} selected</span>
            <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)} direction="down">
                <DropdownToggle tag="span" className="bulk-task-actions-trigger">
                    Actions <i className="fa fa-caret-down" aria-hidden="true" />
                </DropdownToggle>
                <DropdownMenu className="bulk-task-actions-menu" container="body">
                    <div className="menu-item" onClick={() => { setDropdownOpen(false); setOpenModal("status") }}>
                        <i className="fa fa-check-square-o" aria-hidden="true" /> Update Status
                    </div>
                    <div className="menu-item" onClick={() => { setDropdownOpen(false); setOpenModal("dueDate") }}>
                        <i className="fa fa-calendar" aria-hidden="true" /> Update Due Date
                    </div>
                    <div className="menu-item" onClick={() => { setDropdownOpen(false); setOpenModal("assignRep") }}>
                        <i className="fa fa-user-plus" aria-hidden="true" /> Assign Rep
                    </div>
                    <div className="menu-item" onClick={() => { setDropdownOpen(false); setOpenModal("unassignRep") }}>
                        <i className="fa fa-user-times" aria-hidden="true" /> Unassign Rep
                    </div>
                    {(!allSelectedArchived || !allSelectedUnarchived) && <hr className="m-0" />}
                    {!allSelectedArchived && (
                        <div className="menu-item" onClick={() => { setDropdownOpen(false); setOpenModal("archive") }}>
                            <i className="fa fa-archive" aria-hidden="true" /> Archive
                        </div>
                    )}
                    {!allSelectedUnarchived && (
                        <div className="menu-item" onClick={() => { setDropdownOpen(false); setOpenModal("unarchive") }}>
                            <i className="fa fa-undo" aria-hidden="true" /> Unarchive
                        </div>
                    )}
                </DropdownMenu>
            </Dropdown>
            <Button color="link" className="bulk-task-actions-clear" onClick={clearSelectionAndReset}>Clear Selection</Button>

            <Modal isOpen={openModal === "status"} toggle={() => !loading && cancelModal()}>
                <ModalHeader>Update Status for {taskCountLabel}</ModalHeader>
                <ModalBody>
                    <FormGroup className="mb-0">
                        <Label>Status</Label>
                        <Select
                            options={statusOptions}
                            value={statusOptions.find((o) => o.value === status) || null}
                            onChange={(selected) => setStatus(selected ? selected.value : "")}
                        />
                    </FormGroup>
                    {error && <Alert color="danger" className="mb-0 mt-3">{error}</Alert>}
                </ModalBody>
                <ModalFooter>
                    <Button onClick={cancelModal} disabled={loading}>Cancel</Button>
                    <Button color="primary" onClick={handleUpdateStatus} disabled={loading || !status}>
                        {loading ? "Updating..." : "Update Status"}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={openModal === "dueDate"} toggle={() => !loading && cancelModal()}>
                <ModalHeader>Update Due Date for {taskCountLabel}</ModalHeader>
                <ModalBody>
                    <FormGroup className="mb-0">
                        <Label>Due Date</Label>
                        <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                    </FormGroup>
                    {error && <Alert color="danger" className="mb-0 mt-3">{error}</Alert>}
                </ModalBody>
                <ModalFooter>
                    <Button onClick={cancelModal} disabled={loading}>Cancel</Button>
                    <Button color="primary" onClick={handleUpdateDueDate} disabled={loading || !dueDate}>
                        {loading ? "Updating..." : "Update Due Date"}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={openModal === "assignRep"} toggle={() => !loading && cancelModal()}>
                <ModalHeader>Assign Rep to {taskCountLabel}</ModalHeader>
                <ModalBody>
                    <FormGroup className="mb-0">
                        <Label>Rep(s) to add</Label>
                        <RepsTypeAhead onChange={setRepIds} defaultValue={repIds} group={[]} boardId={boardId || null} />
                        <div className="text-muted small mt-2">
                            This adds the selected rep(s) to each task&apos;s existing assignment — it won&apos;t remove anyone already assigned.
                        </div>
                    </FormGroup>
                    {error && <Alert color="danger" className="mb-0 mt-3">{error}</Alert>}
                </ModalBody>
                <ModalFooter>
                    <Button onClick={cancelModal} disabled={loading}>Cancel</Button>
                    <Button color="primary" onClick={handleAssignRep} disabled={loading || repIds.length === 0}>
                        {loading ? "Assigning..." : "Assign"}
                    </Button>
                </ModalFooter>
            </Modal>

            <ConfirmationPopup
                open={openModal === "unassignRep"}
                message={!successMessage ? `Are you sure you want to remove all assigned reps from ${taskCountLabel}?` : ""}
                loading={loading}
                handleClose={handleConfirmationClose}
                action="Unassign"
                confirmAction={() => handleConfirmedAction("UNASSIGN_REP")}
                color="danger"
                error={error}
                success={successMessage}
            />

            <ConfirmationPopup
                open={openModal === "archive"}
                message={!successMessage ? `Are you sure you want to archive ${taskCountLabel}?` : ""}
                loading={loading}
                handleClose={handleConfirmationClose}
                action="Archive"
                confirmAction={() => handleConfirmedAction("ARCHIVE")}
                color="danger"
                error={error}
                success={successMessage}
            />

            <ConfirmationPopup
                open={openModal === "unarchive"}
                message={!successMessage ? `Are you sure you want to unarchive ${taskCountLabel}?` : ""}
                loading={loading}
                handleClose={handleConfirmationClose}
                action="Unarchive"
                confirmAction={() => handleConfirmedAction("UNARCHIVE")}
                color="primary"
                error={error}
                success={successMessage}
            />
        </div>
    )
}

export default withApolloClient(BulkTaskActionsBar)
